import {Link} from '@primer/react'
import type {PublicUser} from '../../../types/public-user'
import styles from './UserInitialMessage.module.css'
import {clsx} from 'clsx'
import type {Session} from '../../../types/session'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ExpandableMarkdown} from '../../expandable-markdown/ExpandableMarkdown'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {ownerAvatarPath} from '@github-ui/paths'
import {useAgentInfo} from '../../../hooks/use-agent-info'
import {createMarkdownWithAgentLinks} from '../../../utils/markdown-link-utils'
import {useIntentDetection} from '../../../hooks/useIntentDetection'
import {AgentAvatar} from '../../AgentAvatar'

const mobileEventTypes = [
  'ios_mobile_mission_control',
  'android_mobile_mission_control',
  'ios_mobile_repo_profile',
  'android_mobile_repo_profile',
]

function getDisplayName(
  isCurrentUserTheCreator: boolean,
  session: Session,
  isTaskCreatedByAutomation?: boolean,
  customAgentDisplayName?: string,
) {
  if (isTaskCreatedByAutomation) return customAgentDisplayName ?? 'Automation'
  if (isCurrentUserTheCreator) return 'You'
  if (session.user_login) return session.user_login
  return 'User'
}

function getAvatarUrl(session: Session, currentUser?: PublicUser, isCurrentUserTheCreator?: boolean) {
  // Prefer the current user's avatar URL for enterprise correctness
  if (isCurrentUserTheCreator && currentUser?.primaryAvatarUrl) return currentUser.primaryAvatarUrl
  return ownerAvatarPath({owner: session.user_login ?? 'ghost'})
}

interface UserInitialMessageProps {
  threadId?: string
  currentUser?: PublicUser
  session: Session
  message: string
  agentId?: number | null
  isTaskCreatedByAutomation?: boolean
  automationDisplayName?: string
}

export const UserInitialMessage = ({
  currentUser,
  threadId,
  session,
  message,
  agentId,
  isTaskCreatedByAutomation,
  automationDisplayName,
}: UserInitialMessageProps) => {
  const {data: agentInfo} = useAgentInfo(agentId)
  const generateOrigin = (linkText: string, url?: string | null, article?: string) => {
    const prefix = article ? ` from ${article} ` : ' from '

    return (
      <>
        {prefix}
        {url ? (
          <Link
            href={url}
            inline
            onClick={() => {
              sendEvent('agent_sessions.task_view', {
                target: 'VIEW_SESSION_LINK',
                session_id: session.id,
                task_id: session.resource_global_id,
                thread_id: threadId,
              })
            }}
          >
            {linkText}
          </Link>
        ) : (
          <span>{linkText}</span>
        )}
      </>
    )
  }

  const eventInformation = () => {
    const type = session.event_type
    const identifiers = session.event_identifiers
    const url = session.event_url

    // Handle conversation threads that don't have an event_url yet
    if (threadId) {
      return generateOrigin('conversation', `${window.location.origin}/copilot/c/${threadId}`, 'a')
    }

    if (!type) {
      return null
    }

    // Event identifiers that we want to show are in the format of `signal::additional::information`
    // and will be handled on a case by case basis dependent on the type
    if (type === 'fix_failed_workflow' || type === 'workflow_run_failed') {
      return generateOrigin('GitHub Actions', url)
    } else if (type === 'issues_agent_assignment' || type === 'issue_created') {
      const issueNumber = identifiers?.find(id => id.startsWith('issue::'))?.split('::')[1]

      if (!issueNumber) {
        return generateOrigin('issue', url, 'an')
      }

      return generateOrigin(`issue #${issueNumber}`, url)
    } else if (type === 'pull_request_comment' || type === 'pull_request_comment_non_copilot_pull_request') {
      return generateOrigin('pull request comment', url, 'a')
    } else if (type === 'pull_request_review' || type === 'pull_request_review_non_copilot_pull_request') {
      return generateOrigin('pull request review', url, 'a')
    } else if (type === 'interval_triggered') {
      return generateOrigin('scheduled trigger', undefined, 'a')
    } else if (type === 'merge_conflict') {
      return generateOrigin('merge conflict', url, 'a')
    } else if (type === 'linear_webhook') {
      return generateOrigin('Linear', url)
    } else if (type === 'jira_issue') {
      return generateOrigin('Jira', url)
    } else if (mobileEventTypes.includes(type)) {
      return generateOrigin('GitHub Mobile')
    }

    return null
  }

  const content = session.event_content
  const isCurrentUserTheCreator = !isTaskCreatedByAutomation && currentUser?.id === session.user_id
  const displayName = getDisplayName(isCurrentUserTheCreator, session, isTaskCreatedByAutomation, automationDisplayName)
  const avatarUrl = getAvatarUrl(session, currentUser, isCurrentUserTheCreator)

  const {isChatSession} = useIntentDetection(session)

  return (
    <div className={styles.container}>
      <div className={styles.messageBubble}>
        {!isChatSession && (
          <p
            className={clsx(styles.meta, {
              [styles.metaSmall]: !!content,
            })}
          >
            <span>{displayName}</span> {message}
            {eventInformation()}
          </p>
        )}

        {content && (
          <ExpandableMarkdown
            className={styles.agentMentionText}
            markdown={agentInfo ? createMarkdownWithAgentLinks(content, agentInfo) : content}
          />
        )}
      </div>
      <div className={styles.avatarContainer}>
        {isTaskCreatedByAutomation ? (
          <AgentAvatar size={24} agentId={agentId ?? undefined} />
        ) : (
          <GitHubAvatar src={avatarUrl} size={24} />
        )}
      </div>
    </div>
  )
}
